import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppStoreState } from "store/AppStore";
import { useInsurerReportsStore } from "store/InsurerReportsStore";

import GrecoReactGrid, {
  standardCellTypeHandlers,
} from "components/grecoreactgrid/GrecoReactGrid";
import { insurerReportEntriesColumnConfig } from "./insurerReportEntriesColumnConfig";
import InsurerReportEntriesFilterBar from "./InsurerReportEntriesFilterBar";
import InsurerReportEntriesSearchBar from "./InsurerReportEntriesSearchBar";
import SortableHeaderCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/SortableHeaderCellTypeHandler";
import SumHeaderCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/SumHeaderCellTypeHandler";
import CustomTextCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/CustomTextCellTypeHandler";
import CustomNumberCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/CustomNumberCellTypeHandler";
import OrdinalNumberCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/OrdinalNumberCellTypeHandler";
import CustomCheckmarkCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/CustomCheckmarkCellTypeHandler";
import InsurerReportEntryOptionsCellTypeHandler from "./customcelltypehandlers/InsurerReportEntryOptionsCellTypeHandler";
import { MatchInsurerReportEntryDialog } from "./MatchInsurerReportEntryDialog";
import { InfoMatchedTargetDialog } from "./InfoMatchedTargetDialog";
import { useTheme } from "@emotion/react";

// Detail pane of the master/detail page: the entries of the selected insurer
// report (loaded by selectInsurerReport), with their own search box and the
// perfect-match toggle.
const InsurerReportEntryGrid = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { taxonomy, grecoCompanies, updatedGrecoCompanies } =
    useAppStoreState();
  const insurerReportsStore = useInsurerReportsStore();
  const [
    {
      insurerReportEntriesSort,
      insurerReportEntryMetadataMap,
      insurerReportEntries,
      entriesSearchTerm,
      includePerfectMatches,
      matchInsurerReportEntryDialog,
      infoInsurerReportEntryDialog,
    },
    {
      setInsurerReportsStoreValues,
      setInsurerReportsStoreValue,
      filterInsurerReportEntries,
    },
  ] = insurerReportsStore;

  // visibility of the loaded entries = search term + perfect-match toggle
  useEffect(() => {
    filterInsurerReportEntries({ entriesSearchTerm, includePerfectMatches });
  }, [insurerReportEntries, entriesSearchTerm, includePerfectMatches]);

  const handleChanges = (
    updatedRows: any[],
    updatedRowsMetadataMap: { [key: number]: any }
  ) => {
    setInsurerReportsStoreValues({
      insurerReportEntries: insurerReportEntries.map((entry) => {
        const updatedEntry = updatedRows.find(
          (row) => row.insurerReportEntryId === entry.insurerReportEntryId
        );
        return updatedEntry ? updatedEntry : entry;
      }),
      insurerReportEntryMetadataMap: updatedRowsMetadataMap,
    });
  };

  const cellTypeHandlers = {
    ...standardCellTypeHandlers,
    sortableheader: new SortableHeaderCellTypeHandler(),
    sumheader: new SumHeaderCellTypeHandler(),
    customtext: new CustomTextCellTypeHandler(),
    customnumber: new CustomNumberCellTypeHandler(),
    ordinalnumber: new OrdinalNumberCellTypeHandler(),
    customcheckmark: new CustomCheckmarkCellTypeHandler(),
    insurerreportentryoptions: new InsurerReportEntryOptionsCellTypeHandler(),
  };

  return (
    <>
      <div style={{ height: "100%", overflow: "auto" }}>
        <InsurerReportEntriesSearchBar />
        <div
          data-help="insurer-reports-entries-grid"
          style={{ height: "calc(100% - 115px)", overflow: "scroll" }}
        >
          <GrecoReactGrid
            rows={insurerReportEntries}
            columns={insurerReportEntriesColumnConfig}
            idColumn={"insurerReportEntryId"}
            rowsMetadataMap={insurerReportEntryMetadataMap}
            handleChanges={handleChanges}
            cellTypeHandlers={cellTypeHandlers}
            hasSumHeaderRow={true}
            hasSelectColumn={false}
            keyColumn="insurerReportEntryId"
            stickyColumns={{
              left: 4,
              right: 1,
              top: 1,
              bottom: 1,
            }}
            context={{
              taxonomy: taxonomy,
              t: t,
              insurerReportsStore: insurerReportsStore,
              theme: theme,
              grecoCompanies: grecoCompanies,
              updatedGrecoCompanies: updatedGrecoCompanies,
              sort: insurerReportEntriesSort,
              setSort: (sort: any) => {
                setInsurerReportsStoreValue(
                  "insurerReportEntriesSort",
                  sort
                );
              },
            }}
            sort={insurerReportEntriesSort}
          />
        </div>
        <InsurerReportEntriesFilterBar />
      </div>
      {matchInsurerReportEntryDialog.isOpen && (
        <MatchInsurerReportEntryDialog />
      )}
      {infoInsurerReportEntryDialog.isOpen && <InfoMatchedTargetDialog />}
    </>
  );
};

export default InsurerReportEntryGrid;
